<template>
  <div ref="rootRef">
    <div class="relative">
      <AppLabel v-if="label" :for="id"
        >{{ label }} <AppRequiredLabel v-if="required"
      /></AppLabel>
      <div
        ref="comboboxRef"
        :class="[
          'min-h-[42px] border border-0.5 rounded-md px-3 py-2 inline-flex w-full',
          error
            ? 'border-red-2 dark:border-dark-red'
            : 'border-grey-11 dark:border-dark-border',
          'focus-within:border-black dark:focus-within:border-dark-ink  ',
          disabled ? 'cursor-not-allowed bg-grey-9 dark:bg-dark-surface-2' : '',
          `bg-${background}`,
          darkControlBgClass(background),
        ]"
        role="combobox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-owns="listboxId"
        aria-haspopup="listbox"
        :aria-activedescendant="
          isOpen ? `${id}-option-${modelValue[valueKey]}` : undefined
        "
        @click="toggleDropdown"
      >
        <input
          :id="id"
          ref="inputRef"
          v-model="searchTerm"
          :disabled="disabled"
          :placeholder="placeholder"
          :class="[
            'w-full bg-transparent focus:outline-hidden text-sm',
            disabled ? 'cursor-not-allowed' : '',
          ]"
          :readonly="!allowSearch"
          :aria-label="label || 'Select input'"
          :aria-invalid="!!error"
          :aria-describedby="
            error ? `${id}-error` : helpLabel ? `${id}-help` : undefined
          "
          @input="onSearch"
        />
        <div class="pointer-events-none flex items-center">
          <AppAutocompleteLabel
            v-if="modelValue?.category"
            class="mr-1"
            :background="modelValue.meta?.background"
            :text="modelValue.meta?.text"
          >
            {{ modelValue.category }}
          </AppAutocompleteLabel>
          <mdicon
            v-if="error"
            name="alert-circle-outline"
            class="text-red-1 dark:text-dark-red pr-2"
            aria-hidden="true"
          />
          <mdicon :name="chevronIcon" :size="20" aria-hidden="true" />
        </div>
      </div>

      <Teleport to="body">
        <ul
          v-if="isOpen"
          :id="listboxId"
          ref="listboxRef"
          :style="panelStyle"
          role="listbox"
          :aria-label="label || 'Select options'"
          class="z-50 bg-white dark:bg-dark-surface shadow-lg rounded-md text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm"
        >
          <li
            v-if="!filteredOptions.length"
            class="py-3 px-4 text-red-1 dark:text-dark-red"
            role="option"
            aria-selected="false"
          >
            {{ noResultsMessage || 'No Results' }}
          </li>
          <li
            v-for="option in filteredOptions"
            :id="`${id}-option-${option[valueKey]}`"
            :key="option[valueKey]"
            role="option"
            :aria-selected="optionSelected(option)"
            class="cursor-pointer items-center flex select-none relative py-3 pl-3 pr-9 hover:bg-grey-9 dark:hover:bg-dark-surface-2 focus:bg-grey-9 dark:focus:bg-dark-surface-2 focus:outline-hidden"
            :class="[
              optionSelected(option) ? 'bg-grey-10 dark:bg-dark-surface-2' : '',
            ]"
            tabindex="0"
            @click="selectOption(option)"
            @keydown.enter="selectOption(option)"
            @keydown.space.prevent="selectOption(option)"
          >
            <span class="justify-start inline-flex flex-1">
              {{ option[labelKey] }}
            </span>
            <span class="justify-end inline-flex items-center">
              <AppAutocompleteLabel
                v-if="option?.category"
                :background="option.meta?.background"
                :text="option.meta?.text"
              >
                {{ option.category }}
              </AppAutocompleteLabel>
              <mdicon
                v-if="optionSelected(option)"
                class="ml-2 text-red-2"
                size="20"
                aria-hidden="true"
                name="check"
              />
            </span>
          </li>
        </ul>
      </Teleport>
    </div>
    <AppErrorLabel v-if="error" :id="`${id}-error`" class="mt-1"
      >{{ error }}
    </AppErrorLabel>
    <AppHelpLabel v-if="helpLabel" :id="`${id}-help`" class="mt-1"
      >{{ helpLabel }}
    </AppHelpLabel>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { darkControlBgClass } from '../../../stores/themeStore.ts'
import { useDropdownPosition } from '@/composables/useDropdownPosition'
import AppAutocompleteLabel from '../autocomplete/AppAutocompleteLabel.vue'

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  allowUnselect: {
    type: Boolean,
    default: true,
  },
  allowSearch: {
    type: Boolean,
    default: true,
  },
  background: {
    type: String,
    default: 'grey-10',
  },
  label: {
    type: String,
    default: '',
  },
  helpLabel: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: { type: Boolean, default: false },
  id: {
    type: String,
    default: () => `select-${Math.random().toString(36).substr(2, 9)}`,
  },
  labelKey: {
    type: String,
    default: 'label',
  },
  valueKey: {
    type: String,
    default: 'value',
  },
  placeholder: {
    type: String,
    default: 'Select an option',
  },
  error: {
    type: String,
    default: '',
  },
  noResultsMessage: {
    type: String,
    default: 'No Results',
  },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const rootRef = ref(null)
const comboboxRef = ref(null)
const listboxRef = ref(null)
const isOpen = ref(false)
const searchTerm = ref('')
const listboxId = `${props.id}-listbox`

const { panelStyle } = useDropdownPosition({
  isOpen,
  triggerRef: comboboxRef,
})

const chevronIcon = computed(() => {
  return isOpen.value ? 'chevron-up' : 'chevron-down'
})

const filteredOptions = computed(() => {
  return props.options.filter((option) =>
    option[props.labelKey]
      ?.toLowerCase()
      .includes(searchTerm?.value?.toLowerCase() || '')
  )
})

const toggleDropdown = () => {
  if (props.disabled) {
    return
  }

  isOpen.value = !isOpen.value
  if (isOpen.value) {
    searchTerm.value = ''
    inputRef.value.focus()
    closeOtherDropdowns()
  }
}

const selectOption = (option) => {
  // If already selected, set to null
  if (optionSelected(option) && props.allowUnselect) {
    emit('update:modelValue', {})
    searchTerm.value = null
  } else {
    emit('update:modelValue', option)
    searchTerm.value = option[props.labelKey]
  }
  isOpen.value = false
}

const onSearch = () => {
  isOpen.value = true
}

const optionSelected = (option) => {
  return option[props.valueKey] == props.modelValue[props.valueKey]
}

const closeDropdown = (e) => {
  // The listbox is teleported to <body>, so check it separately
  if (
    rootRef.value?.contains(e.target) ||
    listboxRef.value?.contains(e.target)
  ) {
    return
  }
  isOpen.value = false
  searchTerm.value = props.modelValue[props.labelKey]
}

const closeOtherDropdowns = () => {
  const event = new CustomEvent('close-dropdowns', { detail: { id: props.id } })
  document.dispatchEvent(event)
}

const handleCloseDropdowns = (e) => {
  if (e.detail.id !== props.id) {
    isOpen.value = false
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    searchTerm.value = newValue[props.labelKey]
  },
  { immediate: true }
)

onMounted(() => {
  document.addEventListener('click', closeDropdown)
  document.addEventListener('close-dropdowns', handleCloseDropdowns)
})

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown)
  document.removeEventListener('close-dropdowns', handleCloseDropdowns)
})
</script>
